import React, { useCallback, useEffect, useReducer } from 'react';
import { Grid } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import strings from '../../Globalization/index';
import { useBreadcrumbs } from '../../contexts/Breadcrumbs';
import connectionService from '../../services/Connection';
import BreadcrumbsComponent from '../../components/common/BreadCrumb';
import ViewHistoryGrid from '../grids/ViewHistory';
import consts from '../../consts';
import CommandModelForm from './CommandModelForm';

const breadcrumbProps = {
	breadcrumbs: [{ text: 'Command' }, { text: '...' }], docTitle: 'Command', actions: [{
		text: 'Delete',
		color: 'error',
		variant: 'contained',
		disabled: true,
		size: 'small',
		startIcon: <DeleteIcon />
	}]
}
const { incubatorDeviceType, biConfigCommand } = consts;
const CommandForm = ({ 
	commandListData, 
	comboTypes = ['Command', 'DataTopics', 'TDataTopics', 'CommandLogLevel', 'CommandServices'], 
	...props 
}) => {
	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const clientCommandId = searchParams.get('clientCommandId') || searchParams.get('id');
	const [, setBreadcrumbs] = useBreadcrumbs();
	const id = searchParams.get('id') || 0;
	const returnTo = searchParams.has('returnTo') ? searchParams.get('returnTo') : '/authorizedclients';
	const [state, setState] = useReducer(
		(prev, next) => (typeof next === 'function' ? { ...prev, ...next(prev) } : { ...prev, ...next }),
		{
			loading: false,
			loadingMessage: strings.DefaultWaitingMsg,
			enableConfigSetting: false,
			title: strings.Command,
			activeRecord: { data: {}, combos: {} },
			json: { data: {} },
			equipmentType: '',
			serialNumber: '',
			clientName: ''
		}
	);

	const handleSetActiveRecord = async (patch) => {
		const activeRecord = typeof patch === 'function'? await patch(state.activeRecord) : patch
		setState({activeRecord})
	};

	useEffect(() => {
		setBreadcrumbs({ ...breadcrumbProps, breadcrumbs: [{ text: 'Command' }, { text: '...' }] });
	}, [searchParams])

	const handleSubmit = (values, { resetForm }) => {
		setState({ loading: true });
		const { Command, Comment, ConfigSetting, swucafile, swucafilesig, ksrootcafile, ksrootcafilesig, clientcert, clientcertsig, clientkey, clientkeysig,Services, DataTopic='[]', TelemetryTopic='[]', CATRootCA, RMRootCA } = values;

		const formattedComboTypes = comboTypes.map((comboType) => ({ type: comboType, loaded: true }));
		const {file} = values;
		const formData = new FormData();
		formData.append('CommandId', Command);
		formData.append('Comment', Comment);
		formData.append('ConfigSetting', ConfigSetting);
		formData.append('ClientCert', clientcert);
		formData.append('ClientCertSig', clientcertsig);
		formData.append('ClientKey', clientkey);
		formData.append('ClientKeySig', clientkeysig);
		formData.append('RootCAPEM', ksrootcafile);
		formData.append('RootCAPEMSig', ksrootcafilesig);
		formData.append('CACertificate', swucafile);
		formData.append('CACertificateSig', swucafilesig);
		formData.append('file', file);
		formData.append('ClientCommandId', clientCommandId);
		formData.append('id', id);
		formData.append('fileName', file?.name);
		formData.append('Services',  JSON.stringify(Services));
		formData.append('action', 'save');
		formData.append('comboTypes', JSON.stringify(formattedComboTypes));
		formData.append('DataTopic', DataTopic);
		formData.append('TelemetryTopic', TelemetryTopic);
		formData.append('RMRootCA', RMRootCA);
		formData.append('CATRootCA', CATRootCA);

		fetch(connectionService.Command, { method: 'POST', body: formData, credentials: 'include' })
			.then((res) => res.json())
			.then(({ success, data, combos, err }) => {
				if (success) {
					setState({ activeRecord: { data, combos }, loading: false });

					Swal.fire({
						title: strings.Success,
						text: strings.SaveSuccessText,
						icon: 'success',
						confirmButtonText: strings.Ok,
						confirmButtonColor: '#3085d6',
						allowOutsideClick: false
					});

					navigate(returnTo);
				} else {
					setState({ loading: false });
					Swal.fire({
						title: strings.ErrorTitle,
						text: strings[err] || strings.FormValidationError,
						icon: 'error',
						confirmButtonText: strings.Ok,
						confirmButtonColor: '#3085d6',
						allowOutsideClick: false
					});
				}
			})
			.finally(() => setState({ loading: true }))
			.catch((error) => {
				setState({ loading: false });
			});
	};

	useEffect(() => {
		if (commandListData) {
			setState({ loading: false });
			Swal.fire({
				title: strings.Success,
				text: strings.CommandSent,
				icon: 'success',
				confirmButtonText: strings.OK,
				confirmButtonColor: '#3085d6',
				allowOutsideClick: false
			});
		}
	}, [commandListData]);
	const fetchCommandData = useCallback(async () => {
		setState({ loading: true });
		const combosArr = comboTypes?.map((comboType) => ({ type: comboType, loaded: true }));
		const formData = new FormData();
		formData.append('action', 'load');
		formData.append('id', 0);
		formData.append('clientCommandId', clientCommandId);
		formData.append('comboTypes', JSON.stringify(combosArr));
		try {
			const res = await fetch(connectionService.Command, { method: 'POST', body: formData, credentials: 'include' });
			const { success, data, combos } = await res.json();

			if (success) {
				setState({ activeRecord: { record: data, combos } });
				if (clientCommandId) {
					data.ClientCommandId = clientCommandId;
				}
				fetchClientRequests({ data, combos });
			}
		} catch (error) {
			setState({ loading: false });
			Swal.fire({
				title: strings.Error,
				text: strings.FormValidationError,
				icon: 'error',
				confirmButtonText: strings.Ok,
				confirmButtonColor: '#3085d6',
				allowOutsideClick: false
			});
		} finally {
			setState({ loading: false });
		}
	}, [comboTypes, clientCommandId, setState]);

	const fetchClientRequests = async ({ data, combos }) => {
		let { Command } = combos || {};
		const disableValues = Array.isArray(data.commandListData) ? data.commandListData.map((obj) => obj.CommandId) : [];

		if (clientCommandId && Command) {
			const formData = new FormData();
			formData.append('action', 'load');
			formData.append('id', clientCommandId);

			try {
				const response = await fetch(connectionService.AuthorizedClients, {
					method: 'POST',
					body: formData,
					credentials: 'include'
				});

				if (response.status === consts.successResponse) {
					const json = await response.json();
					const { EquipmentType: equipmentType, SerialNumber: serialNumber, ClientName: clientName } = json.data;
					setBreadcrumbs({ ...breadcrumbProps, breadcrumbs: [{ text: 'Command' }, { text: clientName }] });
					// #37621: BiconfigCommand display only for the incubator.
					if (equipmentType !== incubatorDeviceType) {
						Command = Command.filter(command => command.CommandName !== biConfigCommand);
					}
					json.data.Subscription = json.data.Subscription.map(item => {
						const parts = item.split('/');
						if (parts[consts.commandIndex] === consts.hashSign) {
							return `+/${parts[consts.firstItemIndex]}`;
						}
						return item;
					});
					for (const commandInfo of Command) {
						const foundCommand = commandInfo.Permission.Subscribe.find((permission) =>
							json.data.Subscription.includes(permission)
						);
						commandInfo.disabled = Boolean(!foundCommand);
					}
					const activeRecord = state.activeRecord;
					activeRecord.combos = combos || {};
					setState(prev => ({ ...prev, activeRecord, equipmentType, serialNumber, clientName }));
				} else {
					Swal.fire({
						title: strings.Error,
						text: strings.ConnectionError,
						icon: 'error',
						confirmButtonText: strings.Ok,
						confirmButtonColor: '#3085d6',
						allowOutsideClick: false
					});
				}
			} catch (error) {
				console.error('Error fetching client requests:', error.message);
			}
		}
	};

	useEffect(() => {
		fetchCommandData();
	}, []);

	return (
		<Grid container spacing={2}>
			<Grid size={12}>
				<CommandModelForm onSubmit={handleSubmit} activeRecord={{ ...state.activeRecord, record: { equipmentType: state.equipmentType } }} isNew  setActiveRecord={handleSetActiveRecord}/>
			</Grid>
			<Grid size={12}>
				<BreadcrumbsComponent breadcrumbs={[{ text: `ViewHistory` }]} setContextValues={false} standalone />
			</Grid>
			<Grid size={12}>
				<ViewHistoryGrid />
			</Grid>
		</Grid>
	);
};
export default CommandForm;
